import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Bell, Mail, Pencil, Plus, Send } from 'lucide-react'
import {
  useNotificationTemplates,
  useUpsertNotificationTemplate,
  useTestSendNotification,
  useNotificationsAdmin,
} from '@/hooks/data/useNotificationsAdmin'
import { toast } from '@/ui/Toast'
import { userMessage } from '@/api/errors'
import EditModal, { type EditField } from '@/components/crud/EditModal'
import type { EmailTemplate } from '@/types/domain'

const STATUS_COLORS: Record<string, string> = {
  Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Sent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
}

function StatusPill({ status }: { status: string }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'}`}>{status}</span>
}

/** Parses a "Key: value" per-line textarea into the merge-field dictionary TestSendCommand
 * expects — same encoding convention as elsewhere in this app (e.g. ProductEditModal's
 * pipe-delimited variant lines) for a plain-text field standing in for structured input. */
function parseSampleData(text: string): Record<string, string | null> {
  const out: Record<string, string | null> = {}
  for (const line of text.split('\n')) {
    const idx = line.indexOf(':')
    if (idx <= 0) continue
    const key = line.slice(0, idx).trim()
    const value = line.slice(idx + 1).trim()
    if (key) out[key] = value
  }
  return out
}

function TemplatesSection() {
  const templates = useNotificationTemplates()
  const upsert = useUpsertNotificationTemplate()
  const testSend = useTestSendNotification()
  const [editing, setEditing] = useState<EmailTemplate | null>(null)
  const [creatingKey, setCreatingKey] = useState(false)
  const [sendingTo, setSendingTo] = useState<EmailTemplate | null>(null)

  const editFields: EditField[] = [
    ...(creatingKey
      ? [{ key: 'key', label: 'Key', type: 'text' as const, required: true, placeholder: 'e.g. order-confirmation', hint: 'Lowercase letters, digits and hyphens only — this is what event consumers and test-send address it by.' }]
      : []),
    { key: 'subject', label: 'Subject', type: 'text', required: true, placeholder: 'Your order {{OrderNumber}} is confirmed' },
    { key: 'bodyHtml', label: 'Body (HTML)', type: 'textarea', required: true, hint: 'Use {{Token}} placeholders — unknown tokens are left visible rather than blanked, so typos are easy to spot in a test-send.' },
  ]

  const sendFields: EditField[] = [
    { key: 'toEmail', label: 'Send to', type: 'text', required: true, placeholder: 'someone@example.com' },
    { key: 'sampleData', label: 'Sample merge fields', type: 'textarea', placeholder: 'OrderNumber: ORD-1234\nCustomerName: Jane', hint: 'One "Key: value" per line — fills the {{Token}} placeholders for this test only.' },
  ]

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <Mail className="h-5 w-5" /> Email templates
        </h2>
        <button
          onClick={() => setCreatingKey(true)}
          className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white"
        >
          <Plus className="h-4 w-4" /> New template
        </button>
      </div>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Real templates consumed by OrderCreatedConsumer/OrderStatusChangedConsumer/CartAbandonedConsumer — editing one changes what customers actually receive on the next matching event.
      </p>

      {templates.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !templates.data || templates.data.length === 0 ? (
        <p className="text-sm text-gray-500">No templates yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
            <tr>
              <th className="pb-2">Key</th>
              <th className="pb-2">Subject</th>
              <th className="pb-2">Updated</th>
              <th className="pb-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {templates.data.map((t) => (
              <tr key={t.id}>
                <td className="py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{t.key}</td>
                <td className="py-2 text-gray-900 dark:text-white">{t.subject}</td>
                <td className="py-2 text-gray-500">{new Date(t.updatedAt).toLocaleString()}</td>
                <td className="py-2">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setSendingTo(t)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Test send">
                      <Send className="h-4 w-4" />
                    </button>
                    <button onClick={() => setEditing(t)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <EditModal
        isOpen={!!editing || creatingKey}
        onClose={() => {
          setEditing(null)
          setCreatingKey(false)
        }}
        title={editing ? `Edit "${editing.key}"` : 'New template'}
        fields={editFields}
        initialValues={editing ? { subject: editing.subject, bodyHtml: editing.bodyHtml } : {}}
        submitLabel="Save"
        onSubmit={async (values) => {
          const key = editing?.key ?? String(values.key ?? '').trim().toLowerCase()
          try {
            await upsert.mutateAsync({ key, body: { subject: values.subject, bodyHtml: values.bodyHtml } })
            toast.success(`Template "${key}" saved`)
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />

      <EditModal
        isOpen={!!sendingTo}
        onClose={() => setSendingTo(null)}
        title={`Test send "${sendingTo?.key}"`}
        description="Sends a real email via the configured SMTP transport — check the recipient inbox."
        fields={sendFields}
        submitLabel="Send"
        onSubmit={async (values) => {
          if (!sendingTo) return
          try {
            const result = await testSend.mutateAsync({
              templateKey: sendingTo.key,
              toEmail: values.toEmail,
              sampleData: parseSampleData(values.sampleData ?? ''),
            })
            if (result.status === 'Failed') {
              throw new Error(result.error || 'Send failed')
            }
            toast.success(`Sent to ${values.toEmail}`)
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />
    </div>
  )
}

function SentLogSection() {
  const [status, setStatus] = useState('')
  const [channel, setChannel] = useState('')
  const [cursorStack, setCursorStack] = useState<string[]>([])
  const cursor = cursorStack[cursorStack.length - 1]

  const filters = useMemo(() => ({ status: status || undefined, channel: channel || undefined, cursor, pageSize: 20 }), [status, channel, cursor])
  const list = useNotificationsAdmin(filters)

  const resetPaging = () => setCursorStack([])

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <Bell className="h-5 w-5" /> Sent log
      </h2>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">Every attempted send, across every customer — including test-sends (shown as "test send", no owning customer).</p>

      <div className="mb-3 flex flex-wrap gap-2">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            resetPaging()
          }}
          className="rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          <option value="">All statuses</option>
          <option value="Pending">Pending</option>
          <option value="Sent">Sent</option>
          <option value="Failed">Failed</option>
        </select>
        <select
          value={channel}
          onChange={(e) => {
            setChannel(e.target.value)
            resetPaging()
          }}
          className="rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          <option value="">All channels</option>
          <option value="Email">Email</option>
          <option value="Sms">SMS</option>
          <option value="Push">Push</option>
        </select>
      </div>

      {list.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : list.isError ? (
        <p className="text-sm text-red-600">Failed to load notifications.</p>
      ) : !list.data || list.data.items.length === 0 ? (
        <p className="text-sm text-gray-500">No notifications match these filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="pb-2 pr-4">Recipient</th>
                <th className="pb-2 pr-4">Channel</th>
                <th className="pb-2 pr-4">Template</th>
                <th className="pb-2 pr-4">Subject</th>
                <th className="pb-2 pr-4">Status</th>
                <th className="pb-2">Sent / Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {list.data.items.map((n) => (
                <tr key={n.id}>
                  <td className="py-2 pr-4 font-mono text-xs text-gray-600 dark:text-gray-300">
                    {n.customerId ? n.customerId.slice(0, 8) : <span className="italic text-gray-400">test send</span>}
                  </td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{n.channel}</td>
                  <td className="py-2 pr-4 font-mono text-xs text-gray-600 dark:text-gray-300">{n.templateKey}</td>
                  <td className="py-2 pr-4 text-gray-900 dark:text-white">{n.subject ?? <span className="text-gray-400">—</span>}</td>
                  <td className="py-2 pr-4">
                    <StatusPill status={n.status} />
                    {n.status === 'Failed' && n.error && (
                      <span className="ml-2 text-xs text-red-500" title={n.error}>
                        ⚠
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-xs text-gray-500">{new Date(n.sentAt ?? n.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center gap-3">
        {cursorStack.length > 0 && (
          <button onClick={() => setCursorStack((prev) => prev.slice(0, -1))} className="text-sm text-blue-600 hover:underline">
            ← Newer
          </button>
        )}
        {list.data?.hasMore && list.data.nextCursor && (
          <button onClick={() => setCursorStack((prev) => [...prev, list.data!.nextCursor!])} className="text-sm text-blue-600 hover:underline">
            Older →
          </button>
        )}
      </div>
    </div>
  )
}

function NotificationsPage() {
  return (
    <div className="p-2 md:p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Bell className="w-6 h-6" /> Notifications
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Real templates + send log from NotificationService — email only today (SMS/Push are placeholders in the channel enum, not yet sending).
        </p>
      </div>

      <TemplatesSection />
      <SentLogSection />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/notifications/')({ component: NotificationsPage })
